import { NextPage } from 'next'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { FaFacebook, FaLink } from 'react-icons/fa'

import { useAuth } from 'src/providers/AuthProvider'

import { Card } from 'src/components/atoms/Card'
import { Divider } from 'src/components/atoms/Divider'
import { Image } from 'src/components/atoms/Image'
import { ExternalLink } from 'src/components/atoms/Link'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { Page } from 'src/components/layouts/Page'
import { AccountSupportStats } from 'src/components/molecules/AccountSupportStats'
import { ProfileStatsCard } from 'src/components/molecules/ProfileStatsCard'

import { listPlantingProjectsPath, viewProjectPath } from 'src/config/paths'
import { env } from 'src/env/client.mjs'
import { NotFound } from 'src/pages/404/404'
import { NavigationContext } from 'src/types/account'
import { trpc } from 'src/utils/trpc'

const ViewProfile: NextPage = () => {
  const router = useRouter()
  const { loginWithCustomRedirect, isStaff } = useAuth()

  const { uri } = router.query as {
    uri: string
  }

  const { data: profileData, isLoading } = trpc.useQuery(
    ['accounts.getByUri', { uri }],
    { enabled: true }
  )

  const { data: funderData, isLoading: isFunderDataLoading } = trpc.useQuery(
    ['accounts.getFunderStats', { accountId: profileData?.account.Id }],
    { enabled: !!profileData?.account.Id }
  )

  const { data: planterPosts } = trpc.useQuery(
    ['sanity.getPostsByPlanterId', { accountId: profileData?.account?.Id }],
    { enabled: !!profileData?.account.PlanterId }
  )

  const { data: funderPosts } = trpc.useQuery(
    ['sanity.getPostsByFunderId', { accountId: profileData?.account.Id }],
    { enabled: !!profileData?.account.FunderId }
  )

  useEffect(() => {
    if (isStaff) return
    // Only redirect if the account is a planter-only (not also a funder)
    if (
      !isLoading &&
      profileData?.account.PlanterId &&
      !profileData?.account.FunderId
    ) {
      if (
        profileData.account.Planter?.Projects.length === 1 &&
        profileData.account.Planter.Projects[0]?.Uri
      ) {
        loginWithCustomRedirect(
          viewProjectPath({
            routeParams: {
              uri: profileData.account.Planter.Projects[0].Uri
            }
          })
        )
      } else {
        loginWithCustomRedirect(listPlantingProjectsPath())
      }
    }
  }, [isLoading, loginWithCustomRedirect, profileData, isStaff])

  if (isLoading) return null

  // Redirect if profile not found
  if (!profileData) return <NotFound />

  if (
    profileData.account.PlanterId &&
    !profileData.account.FunderId &&
    !isStaff
  ) {
    return null
  }

  const { account } = profileData
  const { Avatar, Banner, Name, Description, Facebook, Website } = account

  // Make sure to prepend URLs with http:// if they don't have it - otherwise the `a` tag will not work correctly
  const facebookUrl =
    Facebook && !Facebook.startsWith('http') ? `http://${Facebook}` : Facebook
  const websiteUrl =
    Website && !Website.startsWith('http') ? `http://${Website}` : Website
  return (
    <Page
      title={Name || 'Profile'}
      metaTitle={Name || 'Profile'}
      metaDescription={Description || undefined}
      image={`${env.NEXT_PUBLIC_ASSETS_DOMAIN}/${Banner}`}
      isFullWidth
      navigationContext={NavigationContext.Public}
    >
      <>
        <div className='hero-rebrand-dark unified relative w-full h-[320px] md:h-[400px] overflow-hidden'>
          <Image
            key={Banner}
            alt={`${Name} profile banner`}
            src={`${env.NEXT_PUBLIC_ASSETS_DOMAIN}/${Banner}`}
            fallbackSrc={`${env.NEXT_PUBLIC_ASSETS_DOMAIN}/assets/img/banners/profile-banner-1.jpg`}
            fill
            className='object-cover'
          />
          <div aria-hidden className='absolute inset-0 bg-black/50' />
        </div>
        <div className='bg-white'>
          <div className='md:container md:mx-auto md:flex md:space-x-8 mx-6 items-start pt-4 pb-8'>
            <Card
              size='medium'
              className='flex flex-col md:w-1/3 relative text-center -top-[150px]'
            >
              <Image
                key={Avatar}
                alt={`${Name} profile picture`}
                src={`${env.NEXT_PUBLIC_ASSETS_DOMAIN}/${Avatar}`}
                fallbackSrc='/img/default.png'
                height={200}
                width={200}
                className='self-center'
              />
              <Divider />
              <LoadingSpinner isLoading={isFunderDataLoading}>
                <ProfileStatsCard funderData={funderData} />
              </LoadingSpinner>
              <div className='flex space-x-4 mt-4 justify-center text-forestDeep'>
                {facebookUrl ? (
                  <ExternalLink color='none' href={facebookUrl}>
                    <FaFacebook fontSize='1.5rem' />
                  </ExternalLink>
                ) : null}
                {websiteUrl ? (
                  <ExternalLink color='none' href={websiteUrl}>
                    <FaLink fontSize='1.5rem' />
                  </ExternalLink>
                ) : null}
              </div>
            </Card>
            <div className='md:w-2/3 -mt-28 md:mt-0 space-y-4'>
              {/* spec hierarchy: page title — using rebrand h3 (not h1/h2) since h1/h2 are uppercase and would shout proper-noun profile names */}
              <div className='text-center md:text-left'>
                <TypographyRebrand variant='h3' color='espresso'>
                  {Name || 'Profile'}
                </TypographyRebrand>
              </div>
              <div className='whitespace-pre-wrap'>
                <TypographyRebrand variant='body' color='espresso'>
                  {Description}
                </TypographyRebrand>
              </div>
              <Divider />
              <AccountSupportStats
                accountId={account.FunderId}
                name={Name}
                funderPosts={funderPosts}
                planterPosts={planterPosts}
              />
              <Divider />
            </div>
          </div>
        </div>
      </>
    </Page>
  )
}

export default ViewProfile
